import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Category as CategoryIcon,
  TrendingUp as TrendingIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { SafeAreaView } from '../layout/SafeAreaView';
import { TopBar } from '../layout/TopBar';
import { useInventory } from '../hooks/useInventory';

export const InventoryReportsPage: React.FC = () => {
  const { theme } = useTheme();
  const { items, loading } = useInventory();
  const [categoryData, setCategoryData] = useState<{ [key: string]: { count: number; total: number } }>({});

  const totalValue = useMemo(() => {
    return items.reduce((sum, item) => {
      const value = (item.unitValue || 0) * (item.quantity || 0);
      return sum + value;
    }, 0);
  }, [items]);

  const totalItems = useMemo(() => {
    return items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  }, [items]);

  const averageValue = useMemo(() => {
    if (items.length === 0) return 0;
    return totalValue / items.length;
  }, [totalValue, items.length]);

  const mostExpensiveItem = useMemo(() => {
    if (items.length === 0) return null;
    return items.reduce((max, item) => {
      const itemValue = (item.unitValue || 0) * (item.quantity || 0);
      const maxValue = (max.unitValue || 0) * (max.quantity || 0);
      return itemValue > maxValue ? item : max;
    });
  }, [items]);

  useEffect(() => {
    const categories: { [key: string]: { count: number; total: number } } = {};

    items.forEach((item) => {
      const category = item.category || 'Sem categoria';
      const value = (item.unitValue || 0) * (item.quantity || 0);

      if (!categories[category]) {
        categories[category] = { count: 0, total: 0 };
      }

      categories[category].count += (item.quantity || 0);
      categories[category].total += value;
    });

    setCategoryData(categories);
  }, [items]);

  const sortedCategories = useMemo(() => {
    return Object.entries(categoryData)
      .sort(([, a], [, b]) => b.total - a.total)
      .slice(0, 5);
  }, [categoryData]);

  if (loading) {
    return (
      <SafeAreaView>
        <TopBar title="Relatórios de Custo" showBackButton />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: theme.colors.primary }} />
        </Box>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView>
      <TopBar title="Relatórios de Custo" showBackButton />
      <Box sx={{ p: 2, pb: 8 }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={6}>
            <Card sx={{ backgroundColor: theme.colors.card, borderRadius: theme.radii.medium }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <MoneyIcon sx={{ color: theme.colors.primary, fontSize: 24 }} />
                  <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                    Valor Total
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ color: theme.colors.text.primary, fontWeight: 700 }}>
                  R$ {totalValue.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={6}>
            <Card sx={{ backgroundColor: theme.colors.card, borderRadius: theme.radii.medium }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <InventoryIcon sx={{ color: theme.colors.primary, fontSize: 24 }} />
                  <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                    Total de Itens
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ color: theme.colors.text.primary, fontWeight: 700 }}>
                  {totalItems}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={6}>
            <Card sx={{ backgroundColor: theme.colors.card, borderRadius: theme.radii.medium }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TrendingIcon sx={{ color: theme.colors.primary, fontSize: 24 }} />
                  <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                    Valor Médio
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ color: theme.colors.text.primary, fontWeight: 700 }}>
                  R$ {averageValue.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={6}>
            <Card sx={{ backgroundColor: theme.colors.card, borderRadius: theme.radii.medium }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CategoryIcon sx={{ color: theme.colors.primary, fontSize: 24 }} />
                  <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                    Categorias
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ color: theme.colors.text.primary, fontWeight: 700 }}>
                  {Object.keys(categoryData).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {mostExpensiveItem && (
          <Card sx={{ backgroundColor: theme.colors.card, borderRadius: theme.radii.medium, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: theme.colors.text.secondary, mb: 2 }}>
                Item Mais Caro
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                    {mostExpensiveItem.itemName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                    {mostExpensiveItem.quantity} x R$ {(mostExpensiveItem.unitValue || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ color: theme.colors.primary, fontWeight: 700 }}>
                  R$ {((mostExpensiveItem.unitValue || 0) * (mostExpensiveItem.quantity || 0)).toFixed(2)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        <Card sx={{ backgroundColor: theme.colors.card, borderRadius: theme.radii.medium }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: theme.colors.text.primary, fontWeight: 600, mb: 2 }}>
              Top 5 Categorias por Valor
            </Typography>
            {sortedCategories.length === 0 ? (
              <Typography sx={{ color: theme.colors.text.secondary, textAlign: 'center', py: 2 }}>
                Nenhuma categoria encontrada
              </Typography>
            ) : (
              sortedCategories.map(([category, data], index) => (
                <Box key={category}>
                  {index > 0 && <Divider sx={{ my: 2 }} />}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                        {category}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                        {data.count} itens
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ color: theme.colors.primary, fontWeight: 700 }}>
                      R$ {data.total.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      mt: 1,
                      height: 6,
                      backgroundColor: theme.colors.background,
                      borderRadius: 3,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        backgroundColor: theme.colors.primary,
                        borderRadius: 3,
                        width: `${(data.total / totalValue) * 100}%`,
                      }}
                    />
                  </Box>
                </Box>
              ))
            )}
          </CardContent>
        </Card>
      </Box>
    </SafeAreaView>
  );
};
